import { Component, OnInit } from '@angular/core';
import { ApiService } from '../core/api.service';
import { DialogService } from '../core/dialog.service';
import { TITLES, graduationYears, passwordStrength } from '../core/app-date.pipe';

@Component({
  selector: 'app-signup',
  styles: ['.auth-banner { display: block; width: 100%; max-width: 100%; height: auto; margin: 0 0 6px; }'],
  template: `
  <div class="auth-wrap scroll">
    <div class="auth-card wide">
      <img class="auth-banner" src="assets/header-banner.png" alt="AAA Academy · College Campus Online">
      <div class="auth-tabs">
        <a routerLink="/signin">Sign In</a>
        <a class="active">Sign Up</a>
      </div>

      <div *ngIf="done" class="alert ok big">
        {{ done }}
        <p><a routerLink="/signin" class="btn primary">Go to Sign In</a></p>
      </div>

      <form *ngIf="!done" (ngSubmit)="submit()" #f="ngForm">
        <div class="band">Getting Started: Create An Account</div>

        <label class="req">I am a</label>
        <div class="role-pick">
          <label *ngFor="let r of roles" class="role-opt" [class.on]="m.roleCode === r.roleCode">
            <input type="radio" name="roleCode" [value]="r.roleCode" [(ngModel)]="m.roleCode" required>
            <span class="ico">{{ roleIcon(r.roleCode) }}</span>{{ r.roleName }}
          </label>
        </div>

        <div *ngIf="orgs.length > 1">
          <label class="req">Organization</label>
          <select name="org" [(ngModel)]="m.organizationId" required>
            <option [ngValue]="null" disabled>Select organization</option>
            <option *ngFor="let o of orgs" [ngValue]="o.organizationId">{{ o.orgName }}</option>
          </select>
        </div>

        <div class="grid2">
          <div><label class="req">First Name</label><input name="fn" [(ngModel)]="m.firstName" required></div>
          <div><label>Middle Name</label><input name="mn" [(ngModel)]="m.middleName"></div>
          <div><label class="req">Last Name</label><input name="ln" [(ngModel)]="m.lastName" required></div>
          <div>
            <label>Title</label>
            <select name="title" [(ngModel)]="m.title">
              <option [ngValue]="null">Select title</option>
              <option *ngFor="let t of titles" [value]="t">{{ t }}</option>
            </select>
          </div>
        </div>
        <label class="req">Address</label><input name="addr" [(ngModel)]="m.address" required>
        <div class="grid3">
          <div><label class="req">City</label><input name="city" [(ngModel)]="m.city" required></div>
          <div><label class="req">State</label><input name="state" [(ngModel)]="m.state" required></div>
          <div><label class="req">Zip Code</label><input name="zip" [(ngModel)]="m.zipCode" required></div>
        </div>
        <label class="req">Cell Phone</label><input name="phone" [(ngModel)]="m.cellPhone" required type="tel">

        <div class="band">Optional (For Students / Student Athletes only)</div>
        <label>What is your Graduation Class?</label>
        <select name="grad" [(ngModel)]="m.graduationClass">
          <option [ngValue]="null">Not applicable</option>
          <option *ngFor="let y of years" [value]="y">{{ y }}</option>
        </select>

        <div class="band">Sign-Up: Enter Your Information To Secure Your Account</div>
        <label class="req">Username</label>
        <input name="username" [(ngModel)]="m.username" required maxlength="30" autocomplete="username"
               pattern="[A-Za-z0-9._-]{3,30}" (blur)="checkUsername()" (ngModelChange)="userCheck = null"
               placeholder="e.g. john.smith">
        <div class="hint-line" *ngIf="!userCheck">3–30 characters: letters, numbers, dot, underscore or hyphen. You can sign in with this or your email.</div>
        <div class="field-ok" *ngIf="userCheck?.available">✓ {{ userCheck.message }}</div>
        <div class="field-err" *ngIf="userCheck && !userCheck.available">{{ userCheck.message }}</div>

        <label class="req">Email</label><input name="email" [(ngModel)]="m.email" required email type="email" autocomplete="email">

        <ng-container *ngIf="mode === 'MANUAL'">
          <label class="req">Password</label>
          <div class="pwd">
            <input name="pwd" [type]="show ? 'text' : 'password'" [(ngModel)]="m.password" required autocomplete="new-password">
            <button type="button" class="eye" (click)="show = !show">{{ show ? 'Hide' : 'Show' }}</button>
          </div>
          <div class="meter" *ngIf="m.password"><span [ngClass]="strength().toLowerCase()">{{ strength() }}</span></div>
          <label class="req">Verify Password</label>
          <input name="pwd2" [type]="show ? 'text' : 'password'" [(ngModel)]="m.verifyPassword" required autocomplete="new-password">
          <div class="field-err" *ngIf="m.verifyPassword && m.password !== m.verifyPassword">Passwords do not match.</div>
        </ng-container>
        <p class="muted small" *ngIf="mode === 'AUTO'">A secure password will be generated for you and delivered once your account is approved.</p>

        <div class="alert err" *ngIf="error">{{ error }}</div>
        <button class="btn primary block" [disabled]="busy || !f.valid || !m.roleCode || !passwordOk() || (userCheck && !userCheck.available)">{{ busy ? 'Creating…' : 'SIGN UP' }}</button>
      </form>
    </div>
  </div>`
})
export class SignupComponent implements OnInit {
  orgs: any[] = [];
  mode = 'MANUAL';
  titles = TITLES;
  years = graduationYears();
  m: any = { organizationId: null, title: null, graduationClass: null, username: '', roleCode: null };
  /** Filled from the API; this list is the fallback if the server is older. */
  roles: { roleCode: string; roleName: string }[] = [
    { roleCode: 'STUDENT', roleName: 'Student' }, { roleCode: 'PARENT', roleName: 'Parent' },
    { roleCode: 'COACH', roleName: 'Coach' }, { roleCode: 'COUNSELOR', roleName: 'Guidance Counselor' }];
  userCheck: { available: boolean; message: string } = null;
  show = false;
  busy = false;
  error = '';
  done = '';

  constructor(private api: ApiService, private dialog: DialogService) {}

  ngOnInit() {
    this.api.get('/auth/organizations').subscribe(r => {
      this.orgs = r.organizations;
      this.mode = r.passwordSetupMode;
      if (r.signupRoles && r.signupRoles.length) { this.roles = r.signupRoles; }
      if (this.orgs.length) { this.m.organizationId = this.orgs[0].organizationId; }
    }, e => this.error = ApiService.message(e));
  }

  /** Asks the API whether the username is free as soon as the field loses focus. */
  checkUsername() {
    const u = (this.m.username || '').trim();
    if (!u) { this.userCheck = null; return; }
    this.api.get('/auth/username-available', { username: u }).subscribe(r => this.userCheck = r, () => this.userCheck = null);
  }

  roleIcon(code: string) { return ({ STUDENT: '🎓', PARENT: '👪', COACH: '🏅', COUNSELOR: '🧭' } as any)[code] || '👤'; }

  strength() { return passwordStrength(this.m.password); }
  passwordOk() {
    return this.mode === 'AUTO' || (this.m.password && this.m.password === this.m.verifyPassword && this.strength() !== 'Poor');
  }

  submit() {
    this.busy = true;
    this.error = '';
    this.api.post('/auth/signup', this.m).subscribe(
      r => { this.busy = false; this.done = r.message; },
      e => {
        this.busy = false;
        const body = e && e.error;
        if (body && body.code === 'DUPLICATE_ACCOUNT') {
          const lines: string[] = [];
          if (body.contactEmail) { lines.push('Email: ' + body.contactEmail); }
          if (body.contactPhone) { lines.push('Phone: ' + body.contactPhone); }
          this.error = body.message;
          this.dialog.alert({ icon: '⚠', title: 'Account already exists', message: body.message, lines, danger: true });
          return;
        }
        this.error = ApiService.message(e);
      }
    );
  }
}
